import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Wallet,
} from "lucide-react";

import { AccountFormDialog } from "@/components/accounts/account-form-dialog";
import { DeleteAccountDialog } from "@/components/accounts/delete-account-dialog";
import { LastUpdated } from "@/components/data/last-updated";
import { RefreshButton } from "@/components/data/refresh-button";
import { SourceBadge } from "@/components/data/source-badge";
import { SyncStatusPill } from "@/components/data/sync-status-pill";
import { PageHeader } from "@/components/page-header";
import { PlaidEmptyStateCta } from "@/components/settings/plaid-empty-state-cta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { listBankAccounts, listPlaidItems, syncPlaidItem } from "@/lib/api";
import {
  formatAbsoluteDate,
  formatAbsoluteDateTime,
  formatCurrency,
  formatPercent,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  BankAccount,
  BankAccountType,
  PlaidItem,
} from "@finance/shared";

type FilterValue = "all" | BankAccountType;

const TYPE_LABEL: Record<BankAccountType, string> = {
  checking: "Checking",
  savings: "Savings",
  cd: "CD",
};

const FILTERS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "all", label: "All" },
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
  { value: "cd", label: "CD" },
];

interface PageState {
  status: "loading" | "ready" | "error";
  accounts: BankAccount[];
  plaidItems: PlaidItem[];
  error: string | null;
}

const INITIAL: PageState = {
  status: "loading",
  accounts: [],
  plaidItems: [],
  error: null,
};

interface SyncBanner {
  tone: "success" | "error";
  message: string;
}

export function AccountsPage() {
  const [state, setState] = useState<PageState>(INITIAL);
  const [filter, setFilter] = useState<FilterValue>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [deleting, setDeleting] = useState<BankAccount | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncBanner, setSyncBanner] = useState<SyncBanner | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    setState((prev) =>
      prev.status === "ready"
        ? { ...prev, status: "ready", error: null }
        : INITIAL,
    );
    return Promise.all([
      listBankAccounts(signal),
      listPlaidItems(signal).catch(() => [] as PlaidItem[]),
    ])
      .then(([accounts, plaidItems]) => {
        if (signal?.aborted) return;
        setState({ status: "ready", accounts, plaidItems, error: null });
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        setState({
          status: "error",
          accounts: [],
          plaidItems: [],
          error: err instanceof Error ? err.message : "Failed to load accounts",
        });
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Auto-dismiss the sync result banner so it doesn't pin permanently after
  // a one-off action.
  useEffect(() => {
    if (!syncBanner) return;
    const handle = setTimeout(() => setSyncBanner(null), 5000);
    return () => clearTimeout(handle);
  }, [syncBanner]);

  const syncAll = useCallback(async () => {
    if (state.status !== "ready" || state.plaidItems.length === 0) return;
    setSyncing(true);
    setSyncBanner(null);
    let synced = 0;
    let skipped = 0;
    const failures: string[] = [];
    for (const item of state.plaidItems) {
      try {
        const result = await syncPlaidItem(item.item_id);
        synced += result.synced;
        skipped += result.skipped;
        if (result.errors.length > 0) {
          failures.push(`${item.institution_name}: ${result.errors.length} account(s) failed`);
        }
      } catch (err) {
        failures.push(
          `${item.institution_name}: ${err instanceof Error ? err.message : "sync failed"}`,
        );
      }
    }
    await load();
    setSyncing(false);
    if (failures.length > 0) {
      setSyncBanner({
        tone: "error",
        message:
          failures.length === state.plaidItems.length
            ? `Sync failed. ${failures.join("; ")}`
            : `Sync partially completed (${synced} updated). ${failures.join("; ")}`,
      });
      return;
    }
    setSyncBanner({
      tone: "success",
      message:
        synced + skipped === 0
          ? "Sync complete — no accounts returned from Plaid."
          : `Synced ${synced} account${synced === 1 ? "" : "s"}${
              skipped > 0 ? ` (${skipped} skipped)` : ""
            }.`,
    });
  }, [load, state]);

  const filtered = useMemo(() => {
    const rows =
      filter === "all"
        ? state.accounts
        : state.accounts.filter((a) => a.type === filter);
    return [...rows].sort((a, b) =>
      a.last_updated_at < b.last_updated_at
        ? 1
        : a.last_updated_at > b.last_updated_at
          ? -1
          : 0,
    );
  }, [state.accounts, filter]);

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(account: BankAccount) {
    setEditing(account);
    setFormOpen(true);
  }

  function openDelete(account: BankAccount) {
    setDeleting(account);
  }

  function afterMutation() {
    void load();
  }

  const hasPlaidItems = state.plaidItems.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Bank accounts"
        title="Accounts"
        description="Manual and Plaid-synced checking, savings, and CD balances."
        actions={
          <div className="flex items-center gap-2">
            {hasPlaidItems ? (
              <Button
                variant="outline"
                onClick={() => void syncAll()}
                disabled={syncing}
                title={
                  state.plaidItems.length === 1
                    ? `Sync ${state.plaidItems[0]!.institution_name} via Plaid`
                    : `Sync ${state.plaidItems.length} connected institutions via Plaid`
                }
              >
                <RefreshCw
                  className={cn("h-4 w-4", syncing && "animate-spin")}
                  aria-hidden
                />
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
            ) : null}
            <Button onClick={openAdd}>
              <Plus className="h-4 w-4" aria-hidden />
              Add account
            </Button>
          </div>
        }
      />

      {syncBanner ? (
        <p
          role="status"
          className={cn(
            "rounded-md border px-3 py-2 text-xs",
            syncBanner.tone === "success"
              ? "border-success/40 bg-success/10 text-success"
              : "border-destructive/40 bg-destructive/10 text-destructive",
          )}
        >
          {syncBanner.message}
        </p>
      ) : null}

      <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterValue)}>
        <TabsList>
          {FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {state.status === "loading" ? (
        <AccountsSkeleton />
      ) : state.status === "error" ? (
        <ErrorCard message={state.error ?? "Failed to load accounts"} />
      ) : filtered.length === 0 ? (
        <EmptyState
          filter={filter}
          hasAny={state.accounts.length > 0}
          onAdd={openAdd}
        />
      ) : (
        <AccountsTable
          rows={filtered}
          syncing={syncing}
          onEdit={openEdit}
          onDelete={openDelete}
          onRefreshed={afterMutation}
        />
      )}

      <AccountFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        account={editing}
        onSaved={afterMutation}
      />
      <DeleteAccountDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        account={deleting}
        onDeleted={afterMutation}
      />
    </div>
  );
}

interface AccountsTableProps {
  rows: BankAccount[];
  syncing: boolean;
  onEdit: (account: BankAccount) => void;
  onDelete: (account: BankAccount) => void;
  onRefreshed: () => void;
}

function AccountsTable({
  rows,
  syncing,
  onEdit,
  onDelete,
  onRefreshed,
}: AccountsTableProps) {
  const navigate = useNavigate();
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Institution</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead className="text-right">Interest</TableHead>
            <TableHead>CD maturity</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Sync</TableHead>
            <TableHead>Last updated</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const open = () => navigate(`/accounts/${row.id}/transactions`);
            return (
            <TableRow
              key={row.id}
              role="link"
              tabIndex={0}
              aria-label={`Open transactions for ${row.name}`}
              className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0"
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  open();
                }
              }}
            >
              <TableCell className="font-medium text-foreground">
                {row.name}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {TYPE_LABEL[row.type]}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.source === "api" ? (
                  <PlaidInstitutionTag institution={row.institution} />
                ) : (
                  row.institution
                )}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {formatCurrency(row.current_balance)}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {row.type !== "checking" && row.interest_rate != null
                  ? formatPercent(row.interest_rate, 2)
                  : "—"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.type === "cd" && row.cd_maturity_date ? (
                  <span
                    title={formatAbsoluteDateTime(row.cd_maturity_date)}
                  >
                    {formatAbsoluteDate(row.cd_maturity_date)}
                  </span>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                <SourceBadge source={row.source} />
              </TableCell>
              <TableCell>
                <SyncStatusPill
                  source={row.source}
                  lastSyncedAt={row.last_synced_at}
                  lastSyncStatus={row.last_sync_status}
                  syncing={syncing && row.source === "api"}
                />
              </TableCell>
              <TableCell>
                <LastUpdated iso={row.last_updated_at} />
              </TableCell>
              <TableCell className="text-right">
                {/* Stop propagation: the row click navigates to the
                    transactions page, but the refresh button and actions
                    menu need to stay independently clickable. */}
                <div
                  className="flex items-center justify-end gap-1"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {row.source === "api" ? (
                    <RefreshButton
                      resource="bank-accounts"
                      id={row.id}
                      label={row.name}
                      onRefreshed={onRefreshed}
                    />
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Actions for ${row.name}`}
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEdit(row)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => onDelete(row)}
                        className="text-destructive focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function AccountsSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-10 rounded-md bg-muted/60 animate-pulse",
              i === 3 && "w-2/3",
            )}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface EmptyStateProps {
  filter: FilterValue;
  hasAny: boolean;
  onAdd: () => void;
}

function EmptyState({ filter, hasAny, onAdd }: EmptyStateProps) {
  if (hasAny && filter !== "all") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
            <Wallet className="h-5 w-5" aria-hidden />
          </div>
          <p className="text-sm font-medium text-foreground">
            No {TYPE_LABEL[filter as BankAccountType].toLowerCase()} accounts yet.
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            Switch to another tab, or add a new account of this type.
          </p>
          <Button onClick={onAdd} className="mt-2">
            <Plus className="h-4 w-4" aria-hidden />
            Add account
          </Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
          <Wallet className="h-5 w-5" aria-hidden />
        </div>
        <p className="text-sm font-medium text-foreground">
          No bank accounts yet.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Add a checking, savings, or CD account to start tracking your
          balances, or connect your bank with Plaid to sync them automatically.
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <Button onClick={onAdd}>
            <Plus className="h-4 w-4" aria-hidden />
            Add account
          </Button>
          <PlaidEmptyStateCta />
        </div>
      </CardContent>
    </Card>
  );
}

function PlaidInstitutionTag({ institution }: { institution: string }) {
  return (
    <Badge
      variant="outline"
      className="border-accent/30 bg-accent/10 text-accent font-mono text-[10px] uppercase tracking-wide"
      title={`Synced from Plaid · ${institution}`}
    >
      Plaid · {institution}
    </Badge>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-destructive"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Couldn't load accounts
          </p>
          <p className="font-mono text-xs text-muted-foreground">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
